/**
 * Auth Logger - Centralized logging for authentication flow debugging
 * Logs are prefixed with [AUTH] and include timestamps for easier tracking
 * Flag check should be done at call sites before calling authLogger
 */

const LOG_PREFIX = '[AUTH]'

// Store recent logs in memory for debugging (circular buffer of last 50 entries)
const logBuffer = []
const MAX_LOG_ENTRIES = 50

const getTimestamp = () => {
  const now = new Date()
  return now.toISOString()
}

const addToBuffer = (level, area, message, data) => {
  const entry = {
    timestamp: getTimestamp(),
    level,
    area,
    message,
    data,
  }
  logBuffer.push(entry)
  if (logBuffer.length > MAX_LOG_ENTRIES) {
    logBuffer.shift()
  }
}

const formatMessage = (area, message) => {
  return `${LOG_PREFIX}[${getTimestamp()}][${area}] ${message}`
}

const authLogger = {
  /**
   * Log info level message
   * @param {string} area - Area of auth flow (e.g., 'ProtectedRoute', 'AuthProvider', 'SignInCallback')
   * @param {string} message - Log message
   * @param {object} data - Optional data to log
   */
  info: (area, message, data = null) => {
    const formatted = formatMessage(area, message)
    if (data) {
      console.log(formatted, data)
    } else {
      console.log(formatted)
    }
    addToBuffer('info', area, message, data)
  },

  /**
   * Log warning level message
   */
  warn: (area, message, data = null) => {
    const formatted = formatMessage(area, message)
    if (data) {
      console.warn(formatted, data)
    } else {
      console.warn(formatted)
    }
    addToBuffer('warn', area, message, data)
  },

  /**
   * Log error level message
   */
  error: (area, message, data = null) => {
    const formatted = formatMessage(area, message)
    if (data) {
      console.error(formatted, data)
    } else {
      console.error(formatted)
    }
    addToBuffer('error', area, message, data)
  },

  /**
   * Log debug level message (only in non-production)
   */
  debug: (area, message, data = null) => {
    if (process.env.NODE_ENV === 'production') return
    const formatted = formatMessage(area, message)
    if (data) {
      console.debug(formatted, data)
    } else {
      console.debug(formatted)
    }
    addToBuffer('debug', area, message, data)
  },

  /**
   * Get recent logs from buffer (useful for error reporting)
   */
  getRecentLogs: () => {
    return [...logBuffer]
  },

  /**
   * Clear log buffer
   */
  clearLogs: () => {
    logBuffer.length = 0
  },

  /**
   * Log user state summary (sanitized - no tokens)
   */
  logUserState: (area, user) => {
    if (!user) {
      authLogger.info(area, 'User state: null')
      return
    }
    const summary = {
      expired: user.expired,
      expires_at: user.expires_at,
      expires_in: user.expires_in,
      scope: user.scope,
      profile_sub: user.profile?.sub,
      token_type: user.token_type,
    }
    authLogger.info(area, 'User state summary', summary)
  },

}

export default authLogger
